import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './RaceRecommender.css';

interface RaceCard {
  id: number;
  slug: string;
  name: string;
  city: string;
  state: string;
  race_date: string;
  distances: string[];
  distance_from_user_miles?: number;
  registration_url?: string;
  hero_image_url?: string;
  composite_rating?: number;
  total_reviews?: number;
}

interface RecommenderProps {
  lat?: number;
  lng?: number;
  inline?: boolean; // true when embedded in onboarding
  onSelectRace?: (race: RaceCard) => void;
}

const RaceRecommender: React.FC<RecommenderProps> = ({ lat: propLat, lng: propLng, inline, onSelectRace }) => {
  const [recommendations, setRecommendations] = useState<RaceCard[]>([]);
  const [fitnessSummary, setFitnessSummary] = useState<any>(null);
  const [activeRace, setActiveRace] = useState<any>(null);
  const [hasActiveGoal, setHasActiveGoal] = useState(false);
  const [showAfterCurrent, setShowAfterCurrent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [geoLat, setGeoLat] = useState<number | undefined>(propLat);
  const [geoLng, setGeoLng] = useState<number | undefined>(propLng);
  const [geoResolved, setGeoResolved] = useState(propLat != null);
  const [zipInput, setZipInput] = useState('');
  const [locationStatus, setLocationStatus] = useState('');
  const navigate = useNavigate();

  // Auto-detect location if no lat/lng provided
  useEffect(() => {
    if (propLat != null) {
      setGeoLat(propLat);
      setGeoLng(propLng);
      setLocationStatus('location');
      setGeoResolved(true);
      return;
    }
    if (navigator.geolocation) {
      setLocationStatus('detecting');
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setGeoLat(pos.coords.latitude);
          setGeoLng(pos.coords.longitude);
          setLocationStatus('location');
          setGeoResolved(true);
        },
        () => {
          setLocationStatus('denied');
          setGeoResolved(true); // proceed without location
        },
        { timeout: 8000 }
      );
    } else {
      setLocationStatus('unavailable');
      setGeoResolved(true);
    }
  }, [propLat, propLng]);

  const lat = geoLat;
  const lng = geoLng;

  const handleRetryLocation = () => {
    if (navigator.geolocation) {
      setLocationStatus('detecting');
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setGeoLat(pos.coords.latitude);
          setGeoLng(pos.coords.longitude);
          setLocationStatus('location');
        },
        () => setLocationStatus('denied'),
        { timeout: 8000 }
      );
    }
  };

  const handleZipSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!zipInput.trim()) return;
    // Pass zip as location to backend — it will geocode via geocoding_service
    setLocationStatus('zip');
    setGeoLat(undefined);
    setGeoLng(undefined);
    setLoading(true);
    setError('');
    api.getRaceRecommendations(undefined, undefined, undefined, zipInput.trim())
      .then((data: any) => {
        setRecommendations(data.recommendations || []);
        setFitnessSummary(data.fitness_summary);
        setHasActiveGoal(data.has_active_race_goal);
        setActiveRace(data.active_race);
      })
      .catch(() => setError('Could not load recommendations'))
      .finally(() => setLoading(false));
  };

  const fetchRecommendations = async (afterDate?: string) => {
    setLoading(true);
    setError('');
    try {
      const data = await api.getRaceRecommendations(lat, lng, afterDate);
      setRecommendations(data.recommendations || []);
      setFitnessSummary(data.fitness_summary);
      setHasActiveGoal(data.has_active_race_goal);
      setActiveRace(data.active_race);
    } catch (e: any) {
      setError('Could not load recommendations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (geoResolved) {
      fetchRecommendations();
    }
  }, [geoResolved, lat, lng]);

  const handleTrainForRace = (race: RaceCard) => {
    if (onSelectRace) {
      onSelectRace(race);
      return;
    }
    // Navigate to race goal setup with pre-filled data
    const params = new URLSearchParams();
    params.set('race_name', race.name);
    if (race.race_date) params.set('race_date', race.race_date);
    if (race.distances.length > 0) params.set('distance_label', race.distances[0]);
    if (race.city) params.set('city', race.city);
    if (race.state) params.set('state', race.state);
    navigate(`/race-goal?${params.toString()}`);
  };

  const handleShowAfterCurrent = () => {
    setShowAfterCurrent(true);
    if (activeRace?.date) {
      fetchRecommendations(activeRace.date);
    }
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className={`race-rec ${inline ? 'race-rec--inline' : ''}`}>
        <div className="race-rec__loading">
          <div className="spinner"></div>
          <p>Analyzing your fitness and finding races...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`race-rec ${inline ? 'race-rec--inline' : ''}`}>
      {!inline && <h2 className="race-rec__title">Race Recommendations</h2>}

      {!inline && (
        <div className="race-rec__location-bar">
          {locationStatus === 'location' && lat != null && (
            <span className="race-rec__loc-status race-rec__loc-status--ok">Using your location</span>
          )}
          {locationStatus === 'zip' && (
            <span className="race-rec__loc-status race-rec__loc-status--ok">Searching near {zipInput}</span>
          )}
          {(locationStatus === 'denied' || locationStatus === 'unavailable' || (!lat && locationStatus !== 'detecting' && locationStatus !== 'zip')) && (
            <span className="race-rec__loc-status race-rec__loc-status--warn">No location — showing top-rated races nationally</span>
          )}
          <form className="race-rec__zip-form" onSubmit={handleZipSearch}>
            <input
              type="text"
              className="race-rec__zip-input"
              placeholder="Enter ZIP code"
              value={zipInput}
              onChange={(e) => setZipInput(e.target.value)}
              maxLength={10}
            />
            <button type="submit" className="race-rec__zip-btn" disabled={!zipInput.trim()}>Search</button>
          </form>
          {locationStatus !== 'location' && (
            <button className="race-rec__geo-retry" onClick={handleRetryLocation}>Use My Location</button>
          )}
        </div>
      )}

      {fitnessSummary && (
        <div className="race-rec__fitness">
          <span className={`race-rec__level race-rec__level--${fitnessSummary.level}`}>
            {fitnessSummary.level === 'inactive' ? 'Getting Started' :
             fitnessSummary.level === 'beginner' ? 'Building Base' : 'Active Runner'}
          </span>
          <span className="race-rec__stats">
            {fitnessSummary.run_count_3mo} runs in 3 months &middot; {fitnessSummary.avg_weekly_mileage} mi/wk avg
          </span>
          <span className="race-rec__suggested">
            Recommended: {fitnessSummary.suggested_distances.join(', ')}
          </span>
        </div>
      )}

      {hasActiveGoal && activeRace && !showAfterCurrent && (
        <div className="race-rec__active-goal">
          <p>You're training for <strong>{activeRace.name}</strong> on {formatDate(activeRace.date)}.</p>
          <button className="race-rec__after-btn" onClick={handleShowAfterCurrent}>
            Find my next race after that
          </button>
        </div>
      )}

      {error && <p className="race-rec__error">{error}</p>}

      {recommendations.length === 0 && !error && (
        <div className="race-rec__empty">
          <p>No races found in your area for the trainable window (6-16 weeks out).</p>
          <p>Try <a href="/races">searching all races</a> or check back later.</p>
        </div>
      )}

      <div className="race-rec__grid">
        {recommendations.map(race => (
          <div key={race.id} className="race-rec__card">
            <div className="race-rec__card-header">
              <h3 className="race-rec__card-name">{race.name}</h3>
              <span className="race-rec__card-date">{formatDate(race.race_date)}</span>
            </div>
            <div className="race-rec__card-meta">
              <span className="race-rec__card-location">{race.city}, {race.state}</span>
              {race.distance_from_user_miles != null && (
                <span className="race-rec__card-dist">{race.distance_from_user_miles} mi away</span>
              )}
            </div>
            <div className="race-rec__card-distances">
              {race.distances.map(d => (
                <span key={d} className="race-rec__distance-tag">{d}</span>
              ))}
            </div>
            {race.composite_rating != null && race.composite_rating > 0 && (
              <div className="race-rec__card-rating">
                {'*'.repeat(Math.round(race.composite_rating))} ({race.total_reviews} reviews)
              </div>
            )}
            <div className="race-rec__card-actions">
              <button className="race-rec__train-btn" onClick={() => handleTrainForRace(race)}>
                Train for This Race
              </button>
              <a href={`/races/${race.slug}`} className="race-rec__detail-link">View Details</a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RaceRecommender;
